import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, signal } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { WallboardComponent } from '../../screens/entertainment/wallboard/wallboard.component';
import { CalculatorComponent } from './calculator.component';
import { CurrencyConverterComponent } from './currency-converter.component';
import { GamesGalleryComponent } from './games/games-gallery.component';
import { MemoryMatchComponent } from './games/memory-match.component';
import { MiniMinesweeperComponent } from './games/mini-minesweeper.component';
import { Mini2048Component } from './games/mini-2048.component';
import { MiniSudokuComponent } from './games/mini-sudoku.component';
import { MiniChessComponent } from './games/mini-chess.component';
import { MiniConnectFourComponent } from './games/mini-connect-four.component';
import { NumberMatchComponent } from '../../screens/system/number-match/number-match.component';
import { GameDef, GameId } from './games/games.data';
import { ToolboxService } from './toolbox.service';

/** Static metadata for a toolbox drawer tool. */
interface Tool { id: 'calc' | 'fx' | 'games' | 'wallboard'; name: string; desc: string; icon: string; w: number; bodyClass?: string; }
/** A live floating window — a tool (calc/fx/games/wallboard) or a game. */
interface Win { key: string; name: string; iconName?: string; glyph?: string; gameId?: GameId; w: number; bodyClass?: string; x: number; y: number; z: number; }

const TOOLS: Tool[] = [
  { id: 'calc', name: 'Calculator', desc: 'Quick sums', icon: 'Calculator', w: 340 },
  { id: 'fx', name: 'Currency', desc: 'FX converter', icon: 'DollarSign', w: 380, bodyClass: 'fw-body-fx' },
  { id: 'games', name: 'Games', desc: 'Arcade & board', icon: 'Games', w: 720, bodyClass: 'fw-body-games' },
  { id: 'wallboard', name: 'Wallboard', desc: 'Sticky corkboard', icon: 'Board', w: 900, bodyClass: 'fw-body-wb' },
];

/**
 * Toolbox — a drawer that slides down from the top holding quick tools; each
 * opens as a FLOATING, draggable window over the console. The "Games" tool opens
 * a gallery; picking a game opens it in its own floating window. Mounted once at
 * app root; the topbar button toggles it via {@link ToolboxService}. Ported from
 * the OTA-B2B-5 / OTA-B2B-6 design (toolbox.jsx + games.jsx).
 */
@Component({
  selector: 'app-toolbox',
  standalone: true,
  imports: [
    IconComponent, CalculatorComponent, CurrencyConverterComponent, WallboardComponent,
    GamesGalleryComponent, NumberMatchComponent, MemoryMatchComponent, MiniMinesweeperComponent,
    Mini2048Component, MiniSudokuComponent, MiniChessComponent, MiniConnectFourComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './toolbox.component.html',
  styleUrl: './toolbox.component.scss',
})
export class ToolboxComponent {
  readonly svc = inject(ToolboxService);
  readonly tools = TOOLS;

  readonly wins = signal<Win[]>([]);
  private topZ = 920;

  isActive(key: string): boolean { return this.wins().some((w) => w.key === key); }

  openTool(t: Tool): void {
    this.svc.close();
    this.spawnWin({ key: t.id, name: t.name, iconName: t.icon, w: t.w, bodyClass: t.bodyClass });
  }

  /** Open a game (from the gallery) in its own floating window. Closes the
   *  gallery panel so the game takes its place, centred on screen. */
  openGame(g: GameDef): void {
    this.closeWin('games');
    this.spawnWin({ key: 'game:' + g.id, name: g.name, glyph: g.glyph, gameId: g.id, w: g.w, bodyClass: 'fw-body-game' });
  }

  private spawnWin(base: Omit<Win, 'x' | 'y' | 'z'>): void {
    if (this.isActive(base.key)) { this.focusWin(base.key); return; }
    // Centre the window on screen (clamped below the topbar).
    const x = Math.max(20, Math.round((window.innerWidth - base.w) / 2));
    const y = Math.max(72, Math.round((window.innerHeight - 560) / 2));
    this.topZ += 1;
    this.wins.update((ws) => [...ws, { ...base, x, y, z: this.topZ }]);
  }

  closeWin(key: string): void {
    this.wins.update((ws) => ws.filter((w) => w.key !== key));
  }
  focusWin(key: string): void {
    this.topZ += 1;
    const z = this.topZ;
    this.wins.update((ws) => ws.map((w) => (w.key === key ? { ...w, z } : w)));
  }

  /** Drag a window by its title bar. Listeners are torn down on pointerup. */
  startDrag(key: string, ev: PointerEvent): void {
    if ((ev.target as HTMLElement).closest('.fw-close')) return;
    ev.preventDefault();
    this.focusWin(key);
    const w = this.wins().find((x) => x.key === key);
    if (!w) return;
    const start = { mx: ev.clientX, my: ev.clientY, x: w.x, y: w.y, width: w.w };
    const move = (e: PointerEvent) => {
      const nx = Math.max(-start.width + 80, Math.min(window.innerWidth - 80, start.x + (e.clientX - start.mx)));
      const ny = Math.max(0, Math.min(window.innerHeight - 44, start.y + (e.clientY - start.my)));
      this.wins.update((ws) => ws.map((x) => (x.key === key ? { ...x, x: nx, y: ny } : x)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }
}
